import React, { useState, useEffect } from 'react'
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { db } from '../Admin/Config/Config'

export default function CoursePricing() {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCourses()
  }, [])

  const fetchCourses = async () => {
    try {
      const coursesRef = collection(db, "jaffers_english_courses");
      const q = query(
        coursesRef, 
        where('status', '==', true),
        orderBy('order', 'asc')
      )
      const querySnapshot = await getDocs(q)
      
      const coursesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      setCourses(coursesData)
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    return parseInt(price).toLocaleString('en-IN')
  }

  const getDiscountPercentage = (actualFee, offerFee) => {
    const actual = parseInt(actualFee)
    const offer = parseInt(offerFee)
    return Math.round(((actual - offer) / actual) * 100)
  }

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading courses...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black text-white min-h-screen py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our English Learning Courses
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Choose from our comprehensive range of English courses designed to help you master the language at your own pace.
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-emerald-500 transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/20"
            >
              {/* Course Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={course.img_url}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
                {course.is_live && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                      LIVE
                    </span>
                  </div>
                )}
                {course.admission && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      Open
                    </span>
                  </div>
                )}
              </div>

              {/* Course Content */}
              <div className="p-6">
                <div className="mb-4">
                  <span className="inline-block bg-emerald-600/20 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {course.video_category}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                </div>

                {/* Course Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400">{course.chapter_count}</div>
                    <div className="text-sm text-gray-400">Chapters</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400">{course.video_count}</div>
                    <div className="text-sm text-gray-400">Videos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400">{course.access_days}</div>
                    <div className="text-sm text-gray-400">Days Access</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-400">{course.stream_mode}</div>
                    <div className="text-sm text-gray-400">Mode</div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-6">
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-white">
                      ₹{formatPrice(course.offer_fee)}
                    </span>
                  </div>
                  {course.actual_fee !== course.offer_fee && (
                    <div className="flex items-center gap-2">
                      <span className="bg-emerald-600 text-white px-2 py-1 rounded text-sm font-bold">
                        {getDiscountPercentage(course.actual_fee, course.offer_fee)}% OFF
                      </span>
                      <span className="text-sm text-gray-400">
                        Save ₹{formatPrice(parseInt(course.actual_fee) - parseInt(course.offer_fee))}
                      </span>
                    </div>
                  )}
                </div>

                {/* Course Features */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-300">
                    <svg className="w-4 h-4 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {course.access_days} days access
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <svg className="w-4 h-4 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {course.chapter_count} chapters
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <svg className="w-4 h-4 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {course.video_count} video lessons
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <svg className="w-4 h-4 text-emerald-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {course.stream_mode} mode
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {course.is_live && (
                    <a
                      href={course.live_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Join Live Class
                    </a>
                  )}
                </div>

                {/* Course Info */}
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <div className="text-sm text-gray-400">
                    <span>Created by {course.created_by}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {courses.length === 0 && !loading && (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-xl font-semibold text-white mb-2">No Courses Available</h3>
            <p className="text-gray-400">Check back later for new courses.</p>
          </div>
        )}
      </div>
    </div>
  )
}
